import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  SidenavConfiguration,
  SidenavGroup,
  SidenavItem,
  SidenavState,
  SidenavPermissions,
} from '../models/sidenav.interface';
import { SidenavGroupId, SidenavItemId, UserType } from '../enums/sidenav.enum';
import { AuthService } from '../../services/auth.service';
import { UtilService } from '../../../util/util.service';
import { EasyEatHqService } from '../../services/ee-hq.service';
import { ApiService, DEVICE_APP_NAME } from '../../services/api.service';
import { CommonService } from '../../services/common.service';
import { LocalStorageService } from '../../services/localStorage.service';
import { ResponsiveService } from '../../services/responsive.service';
import { CollectionInfrastructureHelperService } from 'src/app/collection-infrastructure/collection-infrastructure-helper.service';
import {
  BUSINESS_MANAGEMENT,
  MENU_MARKETING,
  MENU_PROMOTIONS,
  ORDER_DINING,
  PERFORMANCE_INSIGHTS,
  REPORT_ANALYTICS,
  SETTINGS_SUPPORT,
  ORDER_DASHBOARD,
  TABLE,
  TABLE_SERVICE,
  MODERN_MENU,
  PROMOTIONS,
  REPORTS,
  RESTAURANT_ANALYTICS,
  RESTAURANT_DETAILS,
  MODERN_INVENTORY,
  APP_SETTINGS,
  HELP_CENTER,
  TABLE_BOOKINGS,
  SECTIONS_COUNTERS,
  MODERN_MENU_NEW,
  MODERN_MENU_SELECTED_NEW,
  // Selected icons
  BUSINESS_MANAGEMENT_SELECTED,
  MENU_MARKETING_SELECTED,
  MENU_PROMOTIONS_SELECTED,
  ORDER_DINING_SELECTED,
  PERFORMANCE_INSIGHTS_SELECTED,
  REPORT_ANALYTICS_SELECTED,
  SETTINGS_SUPPORT_SELECTED,
  ORDER_DASHBOARD_SELECTED,
  TABLE_SELECTED,
  TABLE_SERVICE_SELECTED,
  MODERN_MENU_SELECTED,
  PROMOTIONS_SELECTED,
  REPORTS_SELECTED,
  RESTAURANT_ANALYTICS_SELECTED,
  RESTAURANT_DETAILS_SELECTED,
  MODERN_INVENTORY_SELECTED,
  APP_SETTINGS_SELECTED,
  HELP_CENTER_SELECTED,
  TABLE_BOOKINGS_SELECTED,
  SECTIONS_COUNTERS_SELECTED,
  // HQ-specific icons
  HQ_DASHBOARD,
  HQ_DASHBOARD_SELECTED,
  HQ_ORDERS,
  HQ_ORDERS_SELECTED,
  HQ_OUTLETS,
  HQ_OUTLETS_SELECTED,
  HQ_BILLING,
  HQ_BILLING_SELECTED,
  HQ_SETTLEMENT,
  HQ_SETTLEMENT_SELECTED,
  HQ_STAFF_CENTER,
  HQ_STAFF_CENTER_SELECTED,
  HQ_INVENTORY,
  HQ_INVENTORY_SELECTED,
  HQ_E_INVOICE,
  HQ_E_INVOICE_SELECTED,
  HQ_MENU_DISHES,
  HQ_MENU_DISHES_SELECTED,
  HQ_SMS_MARKETING,
  HQ_SMS_MARKETING_SELECTED,
  HQ_LOYALTY,
  HQ_LOYALTY_SELECTED,
  HQ_REFERRAL,
  HQ_REFERRAL_SELECTED,
  HQ_INTEGRATIONS,
  HQ_INTEGRATIONS_SELECTED,
  HQ_HELP_CENTER,
  HQ_HELP_CENTER_SELECTED,
  HQ_RESTAURANT_DETAILS_SELECTED,
  HQ_PROMOTIONS_SELECTED,
  HQ_SETTINGS_SELECTED,
  HQ_RESTAURANT_ANALYTICS_SELECTED,
  HQ_REPORTS_SELECTED,
  ORDER_DASHBOARD_NEW,
  ORDER_DASHBOARD_SELECTED_NEW,
  HQ_CUSTOMER_DISPLAY,
  HQ_CUSTOMER_DISPLAY_SELECTED,
} from '../../../../defaultImages.js';
import { membership } from '../../enums/membership.enum';
import { AUTH_ENUM } from '../../enums/auth.enum';
@Injectable({
  providedIn: 'root',
})
export class SidenavConfigService implements OnDestroy {
  private readonly configSubject = new BehaviorSubject<SidenavConfiguration>({
    groups: [],
    isCollapsed: false,
  });

  private readonly stateSubject = new BehaviorSubject<SidenavState>({
    activeRoute: '',
    notifications: [],
    lockedFeatures: [],
    userType: UserType.POS,
    isCollapsed: false,
    notificationCounts: {},
  });

  private readonly images = {
    BUSINESS_MANAGEMENT,
    MENU_MARKETING,
    MENU_PROMOTIONS,
    ORDER_DINING,
    PERFORMANCE_INSIGHTS,
    REPORT_ANALYTICS,
    SETTINGS_SUPPORT,
    ORDER_DASHBOARD,
    TABLE,
    TABLE_SERVICE,
    MODERN_MENU,
    PROMOTIONS,
    REPORTS,
    RESTAURANT_ANALYTICS,
    RESTAURANT_DETAILS,
    MODERN_INVENTORY,
    APP_SETTINGS,
    HELP_CENTER,
    TABLE_BOOKINGS,
    SECTIONS_COUNTERS,
    // Selected icons
    BUSINESS_MANAGEMENT_SELECTED,
    MENU_MARKETING_SELECTED,
    MENU_PROMOTIONS_SELECTED,
    ORDER_DINING_SELECTED,
    PERFORMANCE_INSIGHTS_SELECTED,
    REPORT_ANALYTICS_SELECTED,
    SETTINGS_SUPPORT_SELECTED,
    ORDER_DASHBOARD_SELECTED,
    TABLE_SELECTED,
    TABLE_SERVICE_SELECTED,
    MODERN_MENU_SELECTED,
    PROMOTIONS_SELECTED,
    REPORTS_SELECTED,
    RESTAURANT_ANALYTICS_SELECTED,
    RESTAURANT_DETAILS_SELECTED,
    MODERN_INVENTORY_SELECTED,
    APP_SETTINGS_SELECTED,
    HELP_CENTER_SELECTED,
    TABLE_BOOKINGS_SELECTED,
    SECTIONS_COUNTERS_SELECTED,
    // HQ-specific icons
    HQ_DASHBOARD,
    HQ_DASHBOARD_SELECTED,
    HQ_ORDERS,
    HQ_ORDERS_SELECTED,
    HQ_OUTLETS,
    HQ_OUTLETS_SELECTED,
    HQ_BILLING,
    HQ_BILLING_SELECTED,
    HQ_SETTLEMENT,
    HQ_SETTLEMENT_SELECTED,
    HQ_STAFF_CENTER,
    HQ_STAFF_CENTER_SELECTED,
    HQ_INVENTORY,
    HQ_INVENTORY_SELECTED,
    HQ_E_INVOICE,
    HQ_E_INVOICE_SELECTED,
    HQ_MENU_DISHES,
    HQ_MENU_DISHES_SELECTED,
    HQ_SMS_MARKETING,
    HQ_SMS_MARKETING_SELECTED,
    HQ_LOYALTY,
    HQ_LOYALTY_SELECTED,
    HQ_REFERRAL,
    HQ_REFERRAL_SELECTED,
    HQ_INTEGRATIONS,
    HQ_INTEGRATIONS_SELECTED,
    HQ_HELP_CENTER,
    HQ_HELP_CENTER_SELECTED,
    HQ_RESTAURANT_DETAILS_SELECTED,
    HQ_PROMOTIONS_SELECTED,
    HQ_SETTINGS_SELECTED,
    HQ_RESTAURANT_ANALYTICS_SELECTED,
    HQ_REPORTS_SELECTED,
    ORDER_DASHBOARD_NEW,
    ORDER_DASHBOARD_SELECTED_NEW,
    MODERN_MENU_NEW,
    MODERN_MENU_SELECTED_NEW,
    HQ_CUSTOMER_DISPLAY,
    HQ_CUSTOMER_DISPLAY_SELECTED,
  };

  public readonly config$ = this.configSubject.asObservable();
  public readonly state$ = this.stateSubject.asObservable();
  public isKioskHq: boolean = false;
  public isFoodCourt: boolean = false;
  private isMyEnvironment: boolean = false;
  private isIdEnvironment: boolean = false;
  private isMobile: boolean = false;
  private isAtleastSettlementEnabled: boolean = false;
  private subscription: Subscription = new Subscription();
  private isHqUser: boolean = false;
  private isKiosk: boolean = false;
  private outletList: any[] = [];
  private atleastOneProSubs: boolean = false;
  private showPromotionsOnPos: boolean = false;
  private showReferralProgram: boolean = false;
  private hqPricing: boolean = false;
  private kioskCashReport: boolean = false;
  private restSettings: any = {};
  private isHQCmmActive: boolean = false;
  private viewCentralMenu: boolean = false;
  private isEEFreeDigitalMenuLeads: boolean = false;
  private pricing: boolean = false;
  private isTableReservationEnabled: boolean = false;
  private showTableLayout: boolean = false;
  private editDeviceDetailsVisible = false;
  private editDeviceRestaurantId = '';
  private editDeviceCheckedId = '';
  private deviceMatchSettled = false;
  private deviceMatchToken = 0;
  constructor(
    private _authService: AuthService,
    private _utilService: UtilService,
    private _hqService: EasyEatHqService,
    private _localStorage: LocalStorageService,
    private _responsiveService: ResponsiveService,
    private _ciHelperService: CollectionInfrastructureHelperService,
    private _apiService: ApiService,
    private _commonService: CommonService
  ) {
    this.initializeConfiguration();
    this.isKioskHq = this._utilService.getIsKioskHq();
    this.isFoodCourt = this._utilService.getIsFoodcourtHq();
    this.isMyEnvironment = this._utilService.isMyEnvironment();
    this.isIdEnvironment = this._utilService.isIdEnvironment();
    this.showPromotionsOnPos = this._utilService.showPromotionsOnPos();
    this.checkScreenSize();
    this.isHqUser = this._hqService.isHqUser();
    this.outletList = JSON.parse(this._localStorage.get('ee_outlets') || '[]');
    this.isKiosk = !!this._utilService.isKiosk();
    this.atleastOneProSubs = this.atleastOneProSubsChecker();
    this.showReferralProgram = this._utilService.getShowReferralProgram();
    this.hqPricing = this._utilService.litePricingHq();
    this.isEEFreeDigitalMenuLeads = this._utilService.isEEFreeDigitalMenuLeads()
    try {
      this.isAtleastSettlementEnabled = this.isHqUser
        ? this.outletList.some(this.isSettlementVisible)
        : false;
    } catch {
      this.isAtleastSettlementEnabled = false;
    }
    if(this.isHqUser){
      const userPermissions = JSON.parse(
        this._localStorage.get('user_permissions') || '[]'
      );
      if (userPermissions) {
        this.viewCentralMenu = userPermissions.includes(
          AUTH_ENUM.CAN_VIEW_HQ_MENU
        );
      }
    }

    const restSettingsSub = this._localStorage
      .watch('restaurant_settings')
      .subscribe(() => {
        this.getRestSettings();
        this.refreshEditDeviceDetailsVisibility();
        if(Object.keys(this.restSettings).length > 0){
          this.generateConfiguration();
        }
      });
    this.subscription.add(restSettingsSub);
    this.subscription.add(
      this._localStorage.watch('restaurant_id').subscribe(() => {
        this.refreshEditDeviceDetailsVisibility();
      })
    );
    this.subscription.add(
      this._localStorage.watch('deviceId').subscribe(() => {
        this.refreshEditDeviceDetailsVisibility();
      })
    );
    this.subscription.add(
      this._commonService.getOutletListObs().subscribe((outletId) => {
        const id = Array.isArray(outletId) ? outletId[0] : outletId;
        if (id) {
          this.refreshEditDeviceDetailsVisibility(id);
        }
      })
    );
    this.refreshEditDeviceDetailsVisibility();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }


  clearConfigAndStateSubject(): void {
    this.configSubject.next({groups: [], isCollapsed: false});
    this.stateSubject.next({activeRoute: '', notifications: [], lockedFeatures: [], userType: UserType.POS || UserType.HQ, isCollapsed: false, notificationCounts: {}});
  }

  private getRestSettings(): void {
    try {
      this.restSettings = JSON.parse(
        this._localStorage.get('restaurant_settings') || '{}'
      );
      this.kioskCashReport =
        this.restSettings?.global?.kiosk_cash_report ?? false;
      this.pricing = this._utilService.litePricing();
      this.isHQCmmActive = !!JSON.parse(
        this._localStorage.get('hq_settings') || '{}'
      )?.menu?.cmm_enable;
      this.isTableReservationEnabled =
        !!this.restSettings?.global?.show_resv_tab_on_pos;
      this.showTableLayout = !!this.restSettings?.global?.table_layout;
    } catch (error) {
      this.restSettings = {};
    }
  }

  private atleastOneProSubsChecker() {
    let pricing = false;
    try {
      const filteredOutlets = this.outletList.filter((outlet) => {
        const plan = outlet?.settings?.global?.plan;
        return plan === membership.PRO || plan === membership.PRO_SUBS;
      });
      pricing = filteredOutlets?.length > 0;
    } catch (error) {
      console.error(error);
    }
    return pricing;
  }

  private isSettlementVisible(x) {
    if (x?.settings?.settlement.visible) return true;
  }

  private initializeConfiguration(): void {
    const isCollapsed = this._localStorage.get('sidenavCollapsed') === 'true';
    const userType = this.determineUserType();

    this.updateState({
      isCollapsed,
      userType,
      lockedFeatures: this.getLockedFeatures(),
    });
    if(Object.keys(this.restSettings).length > 0){
      this.generateConfiguration();
    }
  }

  private checkScreenSize(): void {
    const responsiveSubscription =
      this._responsiveService.mediaBreakpoint$.subscribe((breakPoint) => {
        this.isMobile = breakPoint === 'xs';
      });
    this.subscription.add(responsiveSubscription);
  }

  private determineUserType(): UserType {
    if (this._hqService.isHqUser()) return UserType.HQ;
    if (this._utilService.isKiosk()) return UserType.KIOSK;
    return UserType.POS;
  }

  private getLockedFeatures(): string[] {
    // Implementation would depend on existing locking logic
    return [];
  }

  private refreshEditDeviceDetailsVisibility(restaurantId?: string): void {
    const token = ++this.deviceMatchToken;
    const storedDeviceId = this._utilService.getDeviceUID();
    const deviceId =
      storedDeviceId === 'undefined' || storedDeviceId === 'null'
        ? ''
        : storedDeviceId;
    const selectedRestaurantId =
      restaurantId || this._localStorage.get('restaurant_id') || '';
    if (
      this.deviceMatchSettled &&
      selectedRestaurantId === this.editDeviceRestaurantId &&
      deviceId === this.editDeviceCheckedId &&
      !restaurantId
    ) {
      return;
    }
    if (!deviceId || !selectedRestaurantId) {
      this.editDeviceRestaurantId = selectedRestaurantId;
      this.editDeviceCheckedId = deviceId;
      this.deviceMatchSettled = true;
      this.setEditDeviceDetailsVisible(false);
      return;
    }
    if (selectedRestaurantId !== this.editDeviceRestaurantId) {
      this.setEditDeviceDetailsVisible(false);
    }
    this.editDeviceRestaurantId = selectedRestaurantId;
    this.editDeviceCheckedId = deviceId;
    this.deviceMatchSettled = false;
    this.subscription.add(
      this._apiService
        .getRestaurantDeviceByUid(
          selectedRestaurantId,
          deviceId,
          DEVICE_APP_NAME.MAIN_DISPLAY
        )
        .subscribe((device) => {
          if (token !== this.deviceMatchToken) {
            return;
          }
          this.deviceMatchSettled = true;
          this.setEditDeviceDetailsVisible(!!device);
        })
    );
  }

  private setEditDeviceDetailsVisible(visible: boolean): void {
    if (this.editDeviceDetailsVisible === visible) {
      return;
    }
    this.editDeviceDetailsVisible = visible;
    if (Object.keys(this.restSettings || {}).length > 0) {
      this.generateConfiguration();
    }
  }

  private generateConfiguration(): void {
    const currentState = this.stateSubject.value;
    const groups = this.createGroupsForUserType(currentState.userType);

    this.configSubject.next({
      groups,
      isCollapsed: currentState.isCollapsed,
    });
  }

  private createGroupsForUserType(userType: UserType): SidenavGroup[] {
    switch (userType) {
      case UserType.HQ:
        return this.createHQGroups();
      case UserType.KIOSK:
        return this.createKioskGroups();
      default:
        return this.createPOSGroups();
    }
  }

  private createHQGroups(): SidenavGroup[] {
    return [
      {
        id: SidenavGroupId.MAIN,
        label: 'SIDENAV.BUSINESS_MANAGEMENT',
        icon: this.images.BUSINESS_MANAGEMENT,
        selectedIcon: this.images.BUSINESS_MANAGEMENT_SELECTED,
        order: 1,
        isExpanded: true,
        isCollapsible: true,
        isVisible: true,
        items: [
          {
            id: SidenavItemId.HQ_DASHBOARD,
            label: 'SIDENAV.HQ_DASHBOARD',
            icon: this.images.HQ_DASHBOARD,
            selectedIcon: this.images.HQ_DASHBOARD_SELECTED,
            route: 'hq/dashboard',
            permission: 'CAN_VIEW_HQ_DASHBOARD',
            isVisible: true,
            order: 1,
            userTypes: [UserType.HQ],
          },
          {
            id: SidenavItemId.ORDERS,
            label: 'SIDENAV.ORDERS',
            icon: this.images.ORDER_DASHBOARD_NEW,
            selectedIcon: this.images.HQ_ORDERS_SELECTED,
            route: 'hq/pending-orders',
            isVisible: false,
            order: 2,
            userTypes: [UserType.HQ],
          },
          {
            id: SidenavItemId.OUTLETS,
            label: 'SIDENAV.OUTLETS',
            icon: this.images.HQ_OUTLETS,
            selectedIcon: this.images.HQ_OUTLETS_SELECTED,
            route: 'hq/outlets',
            permission: 'CAN_VIEW_HQ_OUTLETS',
            isVisible: true,
            order: 3,
            userTypes: [UserType.HQ],
          },
          {
            id: SidenavItemId.BILLING,
            label: 'SIDENAV.ACCOUNT_BILLING',
            icon: this.images.HQ_BILLING,
            selectedIcon: this.images.HQ_BILLING_SELECTED,
            route: 'hq/billing',
            permission: 'CAN_VIEW_HQ_BILLING',
            isVisible: this.isMyEnvironment ? true : false,
            order: 4,
            userTypes: [UserType.HQ],
          },
          {
            id: SidenavItemId.SETTLEMENT,
            label: 'SIDENAV.SETTLEMENT',
            icon: this.images.HQ_SETTLEMENT,
            selectedIcon: this.images.HQ_SETTLEMENT_SELECTED,
            route: 'hq/settlement',
            permission: 'CAN_VIEW_HQ_SETTLEMENT',
            isVisible:
              !this.isMobile &&
              ((this._utilService.litePricingHq(true) &&
                this.isMyEnvironment) ||
                this.isIdEnvironment) &&
              this.isAtleastSettlementEnabled
                ? true
                : false,
            order: 5,
            userTypes: [UserType.HQ],
          },
          {
            id: SidenavItemId.STAFF_CENTER,
            label: 'SIDENAV.STAFF_CENTER',
            icon: this.images.HQ_STAFF_CENTER,
            selectedIcon: this.images.HQ_STAFF_CENTER_SELECTED,
            route: 'hq/staff-center',
            permission: 'CAN_VIEW_HQ_STAFF_SETTINGS',
            isVisible: !this.isMobile ? true : false,
            order: 6,
            userTypes: [UserType.HQ],
          },
          {
            id: SidenavItemId.INVENTORY,
            label: 'SIDENAV.INVENTORY',
            icon: this.images.HQ_INVENTORY,
            selectedIcon: this.images.HQ_INVENTORY_SELECTED,
            route: 'hq/inventory',
            permission: 'CAN_VIEW_INVENTORY_LIST',
            isVisible: (!this.isMobile && !this.isKioskHq && !this.isKiosk) ? true : false,
            order: 7,
            userTypes: [UserType.HQ],
          },
          {
            id: SidenavItemId.E_INVOICE,
            label: 'SIDENAV.E_INVOICE',
            icon: this.images.HQ_E_INVOICE,
            selectedIcon: this.images.HQ_E_INVOICE_SELECTED,
            route: 'hq/e-invoice',
            permission: 'CAN_VIEW_HQ',
            isVisible:
              (!this.isKioskHq && this.isMyEnvironment) ? true : false,
            order: 8,
            userTypes: [UserType.HQ],
          },
        ],
      },
      {
        id: SidenavGroupId.REPORTS_ANALYTICS,
        label: 'SIDENAV.PERFORMANCE_INSIGHTS',
        icon: this.images.PERFORMANCE_INSIGHTS,
        selectedIcon: this.images.PERFORMANCE_INSIGHTS_SELECTED,
        order: 2,
        isExpanded: true,
        isCollapsible: true,
        isVisible: true,
        items: [
          {
            id: SidenavItemId.REPORTS,
            label: 'SIDENAV.REPORTS',
            icon: this.images.REPORTS,
            selectedIcon: this.images.HQ_REPORTS_SELECTED,
            route: 'hq/reports',
            permission: 'CAN_VIEW_REPORTS',
            isVisible: (this.isKiosk && this.kioskCashReport) || !this.isKiosk ? true : false,
            order: 1,
            userTypes: [UserType.HQ],
          },
          {
            id: SidenavItemId.ANALYTICS,
            label: 'SIDENAV.ANALYTICS',
            icon: this.images.RESTAURANT_ANALYTICS,
            selectedIcon: this.images.HQ_RESTAURANT_ANALYTICS_SELECTED,
            route: 'hq/analytics',
            permission: 'CAN_VIEW_ANALYTICS',
            // Temporarily hiding Analytics for all HQ users.
            // isVisible: (!this.isKioskHq && !this.isKiosk) ? true : false,
            isVisible: true,
            order: 2,
            userTypes: [UserType.HQ],
          },
        ],
      },
      {
        id: SidenavGroupId.MENU_PROMOTIONS,
        label: 'SIDENAV.MARKETING_ENGAGEMENT',
        icon: this.images.MENU_MARKETING,
        selectedIcon: this.images.MENU_MARKETING_SELECTED,
        order: 3,
        isExpanded: true,
        isCollapsible: true,
        isVisible: true,
        items: [
          {
            id: SidenavItemId.MENU,
            label: 'SIDENAV.MENU_DISHES',
            icon: this.images.HQ_MENU_DISHES,
            selectedIcon: this.images.HQ_MENU_DISHES_SELECTED,
            route: 'hq/menu-management',
            permission: 'CAN_VIEW_HQ_MENU',
            isVisible: (!this.isKiosk && this.isHQCmmActive && this.viewCentralMenu) ? true : false,
            order: 1,
            userTypes: [UserType.HQ],
          },
          {
            id: SidenavItemId.PROMOTIONS,
            label: 'SIDENAV.PROMOTIONS',
            icon: this.images.PROMOTIONS,
            selectedIcon: this.images.HQ_PROMOTIONS_SELECTED,
            route: 'hq/discounts',
            permission: 'CAN_VIEW_PROMOTIONS',
            isVisible: (!this.isKiosk && this.showPromotionsOnPos) ? true : false,
            order: 2,
            userTypes: [UserType.HQ],
          },
          {
            id: 'SMS_MARKETING',
            label: 'SIDENAV.SMS_MARKETING',
            icon: this.images.HQ_SMS_MARKETING,
            selectedIcon: this.images.HQ_SMS_MARKETING_SELECTED,
            route: 'hq/marketing',
            permission: 'CAN_VIEW_MARKETING',
            isVisible: (!this.isKioskHq && this.atleastOneProSubs) ? true : false,
            order: 3,
            userTypes: [UserType.HQ],
          },
          {
            id: SidenavItemId.LOYALTY,
            label: 'SIDENAV.LOYALTY',
            icon: this.images.HQ_LOYALTY,
            selectedIcon: this.images.HQ_LOYALTY_SELECTED,
            route: 'hq/loyaltyProgram',
            permission: 'CAN_VIEW_LOYALTY',
            isVisible: !((this.isKioskHq || this.isFoodCourt) && this.isIdEnvironment) ? true : false,
            order: 4,
            userTypes: [UserType.HQ],
          },
          {
            id: SidenavItemId.REFERRAL,
            label: 'SIDENAV.REFERRAL_PROGRAM',
            icon: this.images.HQ_REFERRAL,
            selectedIcon: this.images.HQ_REFERRAL_SELECTED,
            route: 'hq/referralProgram',
            permission: 'CAN_VIEW_REFERRAL',
            isVisible: (!this.isKioskHq && this.showReferralProgram && !this.hqPricing) ? true : false,
            order: 5,
            userTypes: [UserType.HQ],
          },
        ],
      },
      {
        id: SidenavGroupId.SETTINGS_SUPPORT,
        label: 'SIDENAV.SETTINGS_SUPPORT',
        icon: this.images.SETTINGS_SUPPORT,
        selectedIcon: this.images.HQ_SETTINGS_SELECTED,
        order: 4,
        isExpanded: true,
        isCollapsible: true,
        isVisible: true,
        items: [
          {
            id: 'EDIT_DEVICE_DETAILS',
            label: 'SIDENAV.EDIT_DEVICE_DETAILS',
            icon: this.images.RESTAURANT_DETAILS,
            selectedIcon: this.images.HQ_RESTAURANT_DETAILS_SELECTED,
            route: 'hq/restaurant/edit-device-details',
            permission: 'CAN_VIEW_RESTAURANT_DETAILS',
            isVisible: this.editDeviceDetailsVisible,
            order: 0,
            userTypes: [UserType.HQ],
          },
          {
            id: 'EDIT_RESTAURANT_DETAILS',
            label: 'SIDENAV.EDIT_RESTAURANT_DETAILS',
            icon: this.images.RESTAURANT_DETAILS,
            selectedIcon: this.images.HQ_RESTAURANT_DETAILS_SELECTED,
            route: 'hq/restaurant/restaurant-details',
            permission: 'CAN_VIEW_RESTAURANT_DETAILS',
            isVisible: true,
            order: 1,
            userTypes: [UserType.HQ],
          },
          {
            id: SidenavItemId.OTHER_TOOL,
            label: 'SIDENAV.OTHER_TOOL',
            icon: this.images.HQ_INTEGRATIONS,
            selectedIcon: this.images.HQ_INTEGRATIONS_SELECTED,
            route: 'hq/restaurant-setting',
            isVisible: true,
            order: 2,
            userTypes: [UserType.HQ],
          },
          {
            id: SidenavItemId.DEVICE_CENTER,
            label: 'SIDENAV.DEVICE_CENTER',
            icon: this.images.HQ_INTEGRATIONS,
            selectedIcon: this.images.HQ_INTEGRATIONS_SELECTED,
            route: 'hq/device-center',
            isVisible: true,
            order: 3,
            userTypes: [UserType.HQ],
          },
          {
            id: SidenavItemId.INTEGRATIONS,
            label: 'SIDENAV.INTEGRATIONS',
            icon: this.images.HQ_INTEGRATIONS,
            selectedIcon: this.images.HQ_INTEGRATIONS_SELECTED,
            route: 'hq/integrations',
            permission: 'CAN_VIEW_INTEGRATIONS',
            isVisible: !this.isKioskHq ? true : false,
            order: 4,
            userTypes: [UserType.HQ],
          },
          {
            id: SidenavItemId.CUSTOMER_DISPALY,
            label: 'SIDENAV.CUSTOMER_DISPLAY',
            icon: this.images.HQ_CUSTOMER_DISPLAY_SELECTED,
            selectedIcon: this.images.HQ_CUSTOMER_DISPLAY_SELECTED,
            route: 'hq/customer-display',
            permission: 'CAN_VIEW_CUSTOMER_DISPLAY',
            isVisible: true,
            order: 5,
            userTypes: [UserType.HQ],
          },
          {
            id: SidenavItemId.HELP_CENTER,
            label: 'SIDENAV.HELP_CENTER',
            icon: this.images.HQ_HELP_CENTER,
            selectedIcon: this.images.HQ_HELP_CENTER_SELECTED,
            route: 'hq/help-center',
            isVisible: true,
            order: 6,
            userTypes: [UserType.HQ],
          },
        ],
      },
    ];
  }

  private createPOSGroups(): SidenavGroup[] {
    return [
      {
        id: SidenavGroupId.ORDER_DINING,
        label: 'SIDENAV.ORDER_DINING',
        icon: this.images.ORDER_DINING,
        selectedIcon: this.images.ORDER_DINING_SELECTED,
        order: 1,
        isExpanded: true,
        isCollapsible: true,
        isVisible: true,
        items: [
          {
            id: SidenavItemId.ORDER_DASHBOARD,
            label: 'SIDENAV.ORDERS_DASHBOARD',
            icon: this.images.ORDER_DASHBOARD_NEW,
            selectedIcon: this.images.ORDER_DASHBOARD_SELECTED_NEW,
            route: !this.handleBlocked('orders') ? '/orders' : null,
            isVisible: true,
            order: 1,
            isLocked: this.handleBlocked('orders'),
            userTypes: [UserType.POS],
          },
          {
            id: SidenavItemId.TABLES,
            label: 'SIDENAV.TABLES',
            icon: this.images.TABLE,
            selectedIcon: this.images.TABLE_SELECTED,
            route: !this.handleBlocked('table-layout') ? '/tables' : null,
            permission: 'CAN_VIEW_TABLE_SETTINGS',
            isVisible: this.showTableLayout && !this.isMobile,
            isLocked: this.handleBlocked('table-layout'),
            order: 3,
            userTypes: [UserType.POS],
          },
          {
            id: SidenavItemId.TABLE_BOOKINGS,
            label: 'SIDENAV.TABLE_BOOKINGS',
            icon: this.images.TABLE_BOOKINGS,
            selectedIcon: this.images.TABLE_BOOKINGS_SELECTED,
            route: !this.handleBlocked('table-bookings') ? '/table-bookings' : null,
            permission: 'CAN_VIEW_TABLE_SERVICE',
            isVisible: this.isTableReservationEnabled,
            isLocked: this.handleBlocked('table-bookings'),
            order: 4,
            userTypes: [UserType.POS],
          },
          {
            id: SidenavItemId.TABLE_SERVICE,
            label: 'SIDENAV.TABLE_SERVICE',
            icon: this.images.TABLE_SERVICE,
            selectedIcon: this.images.TABLE_SERVICE_SELECTED,
            route: !this.handleBlocked('tableservice') ? '/tableservice' : null,
            isVisible: !this.pricing,
            isLocked: this.handleBlocked('tableservice'),
            order: 2,
            userTypes: [UserType.POS],
          },
          {
            id: SidenavItemId.SECTIONS_COUNTERS,
            label: 'SIDENAV.SECTIONS_COUNTERS',
            icon: this.images.SECTIONS_COUNTERS,
            selectedIcon: this.images.SECTIONS_COUNTERS_SELECTED,
            route: !this.handleBlocked('sectionsAndCounters') ? '/section-counters' : null,
            permission: 'CAN_VIEW_SECTION_SETTINGS',
            isVisible: true,
            isLocked: this.handleBlocked('sectionsAndCounters'),
            order: 1,
            userTypes: [UserType.POS],
          },
        ],
      },
      {
        id: SidenavGroupId.REPORTS_ANALYTICS,
        label: 'SIDENAV.REPORTS_ANALYTICS',
        icon: this.images.REPORT_ANALYTICS,
        selectedIcon: this.images.REPORT_ANALYTICS_SELECTED,
        order: 2,
        isExpanded: true,
        isCollapsible: true,
        isVisible: true,
        items: [
          {
            id: SidenavItemId.REPORTS,
            label: 'SIDENAV.REPORTS',
            icon: this.images.REPORTS,
            selectedIcon: this.images.REPORTS_SELECTED,
            route: !this.handleBlocked('reports') ? '/reports' : null,
            permission: 'CAN_VIEW_REPORT',
            isVisible: true,
            order: 1,
            isLocked: this.handleBlocked('reports'),
            userTypes: [UserType.POS],
          },
          {
            id: SidenavItemId.ANALYTICS,
            label: 'SIDENAV.ANALYTICS',
            icon: this.images.RESTAURANT_ANALYTICS,
            selectedIcon: this.images.RESTAURANT_ANALYTICS_SELECTED,
            route: !this.handleBlocked('restaurantAnalytics') ? '/analytics' : null,
            permission: 'CAN_VIEW_RESTAURANT_ANALYTICS',
            isLocked: this.handleBlocked('restaurantAnalytics'),
            isVisible: true,
            order: 2,
            userTypes: [UserType.POS],
          },
        ],
      },
      {
        id: SidenavGroupId.MENU_PROMOTIONS,
        label: 'SIDENAV.MENU_PROMOTIONS',
        icon: this.images.MENU_PROMOTIONS,
        selectedIcon: this.images.MENU_PROMOTIONS_SELECTED,
        order: 3,
        isExpanded: true,
        isCollapsible: true,
        isVisible: true,
        items: [
          {
            id: SidenavItemId.MENU,
            label: 'SIDENAV.MENU',
            icon: this.images.MODERN_MENU_NEW,
            selectedIcon: this.images.MODERN_MENU_SELECTED_NEW,
            route: !this.handleBlocked('menu') ? '/menu' : null,
            permission: 'CAN_VIEW_MENU_ITEM',
            isVisible: true,
            isLocked: this.handleBlocked('menu'),
            order: 1,
            userTypes: [UserType.POS],
          },
          {
            id: SidenavItemId.PROMOTIONS,
            label: 'SIDENAV.PROMOTIONS',
            icon: this.images.PROMOTIONS,
            selectedIcon: this.images.PROMOTIONS_SELECTED,
            route: !this.handleBlocked('promotions') ? '/discounts' : null,
            permission: 'CAN_VIEW_PROMOTION',
            isVisible: this.showPromotionsOnPos,
            isLocked: this.handleBlocked('promotions'),
            order: 2,
            userTypes: [UserType.POS],
          },
        ],
      },
      {
        id: SidenavGroupId.SETTINGS_SUPPORT,
        label: 'SIDENAV.SETTINGS_SUPPORT',
        icon: this.images.SETTINGS_SUPPORT,
        selectedIcon: this.images.SETTINGS_SUPPORT_SELECTED,
        order: 4,
        isExpanded: true,
        isCollapsible: true,
        isVisible: true,
        items: [
          {
            id: 'EDIT_DEVICE_DETAILS',
            label: 'SIDENAV.EDIT_DEVICE_DETAILS',
            icon: this.images.RESTAURANT_DETAILS,
            selectedIcon: this.images.RESTAURANT_DETAILS_SELECTED,
            route: 'restaurant/edit-device-details',
            permission: 'CAN_VIEW_RESTAURANT_DETAILS',
            isVisible: this.editDeviceDetailsVisible,
            isLocked: false,
            order: 2,
            userTypes: [UserType.POS],
          },
          {
            id: 'EDIT_RESTAURANT_DETAILS',
            label: 'SIDENAV.EDIT_RESTAURANT_DETAILS',
            icon: this.images.RESTAURANT_DETAILS,
            selectedIcon: this.images.RESTAURANT_DETAILS_SELECTED,
            route: 'restaurant/restaurant-details',
            permission: 'CAN_VIEW_RESTAURANT_DETAILS',
            isVisible: true,
            isLocked: false,
            order: 3,
            userTypes: [UserType.POS],
          },
          // {
          //   id: SidenavItemId.APP_SETTINGS,
          //   label: 'SIDENAV.APP_SETTINGS',
          //   icon: this.images.APP_SETTINGS,
          //   selectedIcon: this.images.APP_SETTINGS_SELECTED,
          //   onClick: () => this.handleAppSettings(),
          //   isVisible: !!this._utilService.isReactDevice(),
          //   order: 4,
          //   userTypes: [UserType.POS],
          // },
          {
            id: SidenavItemId.INVENTORY,
            label: 'SIDENAV.INVENTORY',
            icon: this.images.MODERN_INVENTORY,
            selectedIcon: this.images.MODERN_INVENTORY_SELECTED,
            route: !this.handleBlocked('inventory') ? '/inventory' : null,
            permission: 'CAN_VIEW_INVENTORY_LIST',
            isVisible: !this.isMobile,
            isLocked: this.handleBlocked('inventory'),
            order: 2,
            userTypes: [UserType.POS],
          },
          {
            id: SidenavItemId.HELP_CENTER,
            label: 'SIDENAV.HELP_CENTER',
            icon: this.images.HELP_CENTER,
            selectedIcon: this.images.HELP_CENTER_SELECTED,
            route: '/help-center',
            isVisible: true,
            isLocked: this.handleBlocked('helpCentre'),
            order: 5,
            userTypes: [UserType.POS],
          },
        ],
      },
    ];
  }

  private createKioskGroups(): SidenavGroup[] {
    // Simplified groups for kiosk mode
    return [
      {
        id: SidenavGroupId.ORDER_DINING,
        label: 'SIDENAV.ORDER_DINING',
        icon: this.images.ORDER_DINING,
        selectedIcon: this.images.ORDER_DINING_SELECTED,
        order: 1,
        isExpanded: true,
        isCollapsible: true,
        isVisible: true,
        items: [
          {
            id: SidenavItemId.ORDER_DASHBOARD,
            label: 'SIDENAV.ORDERS_DASHBOARD',
            icon: this.images.ORDER_DASHBOARD_NEW,
            selectedIcon: this.images.ORDER_DASHBOARD_SELECTED_NEW,
            route: '/orders',
            isVisible: true,
            order: 1,
            userTypes: [UserType.KIOSK],
          },
          {
            id: SidenavItemId.TABLES,
            label: 'SIDENAV.TABLES',
            icon: this.images.TABLE,
            selectedIcon: this.images.TABLE_SELECTED,
            route: !this.handleBlocked('table-layout') ? '/tables' : null,
            permission: 'CAN_VIEW_TABLE_SETTINGS',
            isVisible: this.showTableLayout && !this.isMobile,
            isLocked: this.handleBlocked('table-layout'),
            order: 2,
            userTypes: [UserType.KIOSK],
          },
          {
            id: SidenavItemId.SECTIONS_COUNTERS,
            label: 'SIDENAV.SECTIONS_COUNTERS',
            icon: this.images.SECTIONS_COUNTERS,
            selectedIcon: this.images.SECTIONS_COUNTERS_SELECTED,
            route: '/section-counters',
            permission: 'CAN_VIEW_SECTION_SETTINGS',
            isVisible: true,
            order: 1,
            userTypes: [UserType.KIOSK],
          },
        ],
      },
      {
        id: SidenavGroupId.REPORTS_ANALYTICS,
        label: 'SIDENAV.REPORTS_ANALYTICS',
        icon: this.images.REPORT_ANALYTICS,
        selectedIcon: this.images.REPORT_ANALYTICS_SELECTED,
        order: 2,
        isExpanded: true,
        isCollapsible: true,
        isVisible: true,
        items: [
          {
            id: SidenavItemId.REPORTS,
            label: 'SIDENAV.REPORTS',
            icon: this.images.REPORTS,
            selectedIcon: this.images.REPORTS_SELECTED,
            route: '/reports',
            permission: 'CAN_VIEW_REPORT',
            isVisible: true,
            order: 1,
            userTypes: [UserType.KIOSK],
          },
        ],
      },
      {
        id: SidenavGroupId.SETTINGS_SUPPORT,
        label: 'SIDENAV.SETTINGS_SUPPORT',
        icon: this.images.SETTINGS_SUPPORT,
        selectedIcon: this.images.SETTINGS_SUPPORT_SELECTED,
        order: 3,
        isExpanded: true,
        isCollapsible: true,
        isVisible: true,
        items: [
          {
            id: 'EDIT_RESTAURANT_DETAILS',
            label: 'SIDENAV.EDIT_RESTAURANT_DETAILS',
            icon: this.images.RESTAURANT_DETAILS,
            selectedIcon: this.images.RESTAURANT_DETAILS_SELECTED,
            route: 'hq/restaurant',
            permission: 'CAN_VIEW_RESTAURANT_DETAILS',
            isVisible: true,
            order: 2,
            userTypes: [UserType.KIOSK],
          },
          // {
          //   id: SidenavItemId.APP_SETTINGS,
          //   label: 'SIDENAV.APP_SETTINGS',
          //   icon: this.images.APP_SETTINGS,
          //   selectedIcon: this.images.APP_SETTINGS_SELECTED,
          //   onClick: () => this.handleAppSettings(),
          //   isVisible: !!this._utilService.isReactDevice(),
          //   order: 3,
          //   userTypes: [UserType.KIOSK],
          // },
          {
            id: SidenavItemId.HELP_CENTER,
            label: 'SIDENAV.HELP_CENTER',
            icon: this.images.HELP_CENTER,
            selectedIcon: this.images.HELP_CENTER_SELECTED,
            route: '/help-center',
            isVisible: true,
            order: 4,
            userTypes: [UserType.KIOSK],
          },
        ],
      },
    ];
  }

  private handleAppSettings(): void {
    // Handle app settings click for React devices
    try {
      const data = { action: 'app_setting' };
      const x = JSON.stringify(data);
      (window as any).send_data(x);
    } catch (error) {
      console.error('Error sending app settings data:', error);
    }
  }

  public updateNotificationCount(itemId: string, count: number): void {
    const currentState = this.stateSubject.value;
    const newCounts = { ...(currentState.notificationCounts || {}) };
    newCounts[itemId] = count;
    this.updateState({ notificationCounts: newCounts });
  }

  public updateNotifications(notifications: any[], id?: string): void {
    this.updateState({ notifications });
    if (id) {
      this.updateNotificationCount(id, notifications.length);
    }
  }

  public updateLockedFeatures(lockedFeatures: string[]): void {
    this.updateState({ lockedFeatures });
  }

  public updateActiveRoute(activeRoute: string): void {
    this.updateState({ activeRoute });
  }

  public toggleCollapse(): void {
    const currentState = this.stateSubject.value;
    const isCollapsed = !currentState.isCollapsed;

    this._localStorage.set('sidenavCollapsed', isCollapsed.toString());
    this.updateState({ isCollapsed });

    const currentConfig = this.configSubject.value;
    this.configSubject.next({
      ...currentConfig,
      isCollapsed,
    });
  }

  public checkPermissions(item: SidenavItem): SidenavPermissions {
    if (!item.permission) {
      return { canView: true, canAccess: true, requiredPermissions: [] };
    }

    const canView = this._authService.isValidPermission(item.permission);
    const canAccess = this._authService.isURLAuthenticated(item.route || '');

    return {
      canView,
      canAccess,
      requiredPermissions: [item.permission],
    };
  }

  private updateState(partialState: Partial<SidenavState>): void {
    const currentState = this.stateSubject.value;
    this.stateSubject.next({ ...currentState, ...partialState });
  }

  public refreshConfiguration(): void {
    // Determine the current user type in case it has changed
    const currentUserType = this.determineUserType();
    const currentState = this.stateSubject.value;

    // Update the user type if it has changed
    if (currentState.userType !== currentUserType) {
      this.updateState({
        userType: currentUserType,
        lockedFeatures: this.getLockedFeatures(),
      });
    }

    this.generateConfiguration();
  }

  /**
   * Force refresh the configuration with updated user type
   * This should be called when switching between HQ and POS
   */
  public refreshConfigurationForUserTypeChange(): void {
    const newUserType = this.determineUserType();
    const isCollapsed = this._localStorage.get('sidenavCollapsed') === 'true';
    // Reload settings so groups rebuild after sidenav is recreated (e.g. leaving /pos).
    this.getRestSettings();

    this.updateState({
      userType: newUserType,
      isCollapsed,
      lockedFeatures: this.getLockedFeatures(),
      activeRoute: '', // Reset active route when switching user types
      notifications: [], // Reset notifications when switching
    });
    this.generateConfiguration();
  }

  public getCurrentState(): SidenavState {
    return this.stateSubject.value;
  }

  public getNotificationCountForItem(itemId: string): number {
    return this.stateSubject.value.notificationCounts?.[itemId] || 0;
  }

  private  handleBlocked(tab) {
    const unlockedTabsForEEMenuLeads = [
      'menu',
      'promotions',
      'helpCentre',
      'ee-hq',
      'outlets',
      'ai-insights'
    ];

    if (this.isEEFreeDigitalMenuLeads) {
     return !unlockedTabsForEEMenuLeads.includes(tab);
    } else if (this._ciHelperService.isPlanExpired()) {
      const blockedFeatures = this._ciHelperService.getBlockedFeatures()
      return blockedFeatures.includes(tab);
    }
    return false;
  }
}
